import { useLazyQuery, useMutation } from '@apollo/client'
import { GET_INTRODUCTION_PAGE, GET_CYBER_INTRODUCTION_PAGE } from "../graphql/queries"
import {
  START_PRACTICE_ASSESSMENT,
  SUBMIT_PRACTICE_ASSESSMENT,
  SUBMIT_ASSESSMENT,
  START_ASSESSMENT,
  CANCEL_ASSESSMENT,
  CANCEL_CYBER_ASSESSMENT,
  SUBMIT_COMMENT,
  SUBMIT_CYBER_COMMENT,
  START_CYBER_ASSESSMENT,
  SUBMIT_CYBER_ASSESSMENT
} from '../graphql/mutations'
import { toast } from 'react-toastify'
import { useNavigate } from "react-router-dom"
import { datadogLogs } from '@datadog/browser-logs';

const useAssessment = () => {
  const [fetchIntroPage] = useLazyQuery(GET_INTRODUCTION_PAGE)
  const [createPracticeTest] = useMutation(START_PRACTICE_ASSESSMENT)
  const [createAssessment] = useMutation(START_ASSESSMENT)
  const [submitPracticeTestResponse] = useMutation(SUBMIT_PRACTICE_ASSESSMENT)
  const [submitAssessmentResponse] = useMutation(SUBMIT_ASSESSMENT)
  const [terminateAssessment] = useMutation(CANCEL_ASSESSMENT)
  const [commentAssessment] = useMutation(SUBMIT_COMMENT)
  
  // Cyber Assessment specific queries and mutations
  const [fetchCyberIntroPage] = useLazyQuery(GET_CYBER_INTRODUCTION_PAGE, {
  fetchPolicy: "network-only"     // as it has to be fetched in case there is some active cyber assessment
  })
  const [createCyberAssessment] = useMutation(START_CYBER_ASSESSMENT)
  const [submitCyberAssessmentResponse] = useMutation(SUBMIT_CYBER_ASSESSMENT)
  const [commentCyberAssessment] = useMutation(SUBMIT_CYBER_COMMENT)
  const [terminateCyberAssessment] = useMutation(CANCEL_CYBER_ASSESSMENT)
  
  const navigate = useNavigate()

  const getIntroPage = (page: number, setIntroPageData: React.Dispatch<React.SetStateAction<IAssessmentIntroduction | undefined>>) => {
    let logDetails: Record<string, any> = {
      pageNumber: page
    };
    datadogLogs.logger.info("Fetching document review assessment intro page...", logDetails);

    fetchIntroPage({
      variables: {
        pageNumber: page
      }
    }).then((response) => {
      datadogLogs.logger.info("Document Review Assessment intro page fetched successfully.");
      console.log(response.data.assessmentIntroduction)
      setIntroPageData(response.data.assessmentIntroduction)
    })
  }

  const getCyberIntroPage = (page: number, setIntroPageData: React.Dispatch<React.SetStateAction<ICyberAssessmentIntroduction | undefined>>) => {
    let logDetails: Record<string, any> = {
      pageNumber: page
    };
    datadogLogs.logger.info("Fetching cyber assessment intro page...", logDetails);

    fetchCyberIntroPage({
      variables: {
        pageNumber: page
      }
    }).then((response) => {
      datadogLogs.logger.info("Cyber assessment intro page fetched successfully.");
      console.log(response.data.cyberAssessmentIntroduction)
      setIntroPageData(response.data.cyberAssessmentIntroduction)
    })
  }

  const startPracticeTest = ({selectPracticeLanguage, setAssessmentData, setError }: ISubmitResponseData) => {
    datadogLogs.logger.info("Starting practice test for document review assessment...");

    createPracticeTest({
      variables: { language: selectPracticeLanguage }
    }).then((response) => {
      datadogLogs.logger.info("Practice test for document review assessment started successfully");
      setAssessmentData(response.data.createPracticeTest)
    })
      .catch((errors) => {
        console.error('Error Starting Practice Test for document review assessment:', errors.graphQLErrors.map((error: any) => error.message))
        setError && setError(errors.graphQLErrors[0].message)
      })
  }

  const submitPracticeTest = ({ attributes, setAssessmentData, setError }: ISubmitResponseData, onSuccess?: Function, onError?: Function) => {
    datadogLogs.logger.info("Submitting practice test for document review assessment...");

    submitPracticeTestResponse({
      variables: {
        attributes: attributes
      }
    })
      .then((response) => {
        datadogLogs.logger.info("Practice test for document review assessment submitted successfully");
        setAssessmentData(response.data.submitPracticeTest)
      })
      .then((response) => {
        if (onSuccess != null) {
          onSuccess()
        }
      })
      .catch((errors) => {
        console.error('Error Submitting Practice Test for document review assessment:', errors.graphQLErrors.map((error: any) => error.message))
        setError && setError(errors.graphQLErrors[0].message)
        if (onError != null) {
          onError()
        }
      })
  }

  const startAssessment = ({ language, setAssessmentData, setError }: ISubmitResponseData) => {
    datadogLogs.logger.info("Starting document review assessment...");

    createAssessment({
      variables: {
        language: language
      }
    }).then((response) => {
      datadogLogs.logger.info("Document Review Assessment started successfully");
      setAssessmentData(response.data.createAssessment)
    })
      .catch((errors) => {
        console.error('Error Starting Document Review Assessment:', errors.graphQLErrors.map((error: any) => error.message))
        setError && setError(errors.graphQLErrors[0].message)
      })
  }

  const cancelAssessment = ({ setStatus, setError }: ICancelAssessmentData) => {
    datadogLogs.logger.info("Terminating Document Review assessment...");

    terminateAssessment().then((response) => {
      datadogLogs.logger.info("Document Review Assessment terminated successfully");
      setStatus(response.data.cancelAssessment)
    })
      .catch((errors) => {
        console.error('Error Starting Assessment:', errors.graphQLErrors.map((error: any) => error.message))
        setError && setError(errors.graphQLErrors[0].message)
      })
  }

  const cancelCyberAssessment = ({ setStatus, setError }: ICancelCyberAssessmentData) => {
    datadogLogs.logger.info("Terminating cyber assessment...");
    terminateCyberAssessment().then((response) => {
      datadogLogs.logger.info("Cyber Assessment terminated successfully");
      setStatus(response.data.cancelCyberAssessment)
    })
      .catch((errors) => {
        console.error('Error Canceling Cyber Assessment:', errors.graphQLErrors.map((error: any) => error.message))
        setError && setError(errors.graphQLErrors[0].message)
      })
  }

  const submitAssessment = ({ language, attributes, setAssessmentData, setError }: ISubmitResponseData, onSuccess?: Function, onError?: Function) => {
    datadogLogs.logger.info("Submitting Document Review assessment...");

    submitAssessmentResponse({
      variables: {
        language: language,
        attributes: attributes
      }
    })
      .then((response) => {
        datadogLogs.logger.info("Document Review Assessment submitted successfully");
        setAssessmentData(response.data.submitAssessment)
      })
      .then((response) => {
        if (onSuccess != null) {
          onSuccess()
        }
      })
      .catch((errors) => {
        console.error('Error Submitting document review Assessment:', errors.graphQLErrors.map((error: any) => error.message))
        setError && setError(errors.graphQLErrors[0].message)
        if (onError != null) {
          onError()
        }
      })
  }

  const submitComment = (comment: string) => {
    datadogLogs.logger.info("Submitting document review assessment comment...");

    commentAssessment({
      variables: {
        comment: comment
      }
    }).then((response) => {
      datadogLogs.logger.info("Document review assessment comment submitted successfully.");
      const submitCommentStatus = response.data.commentAssessment
      navigate('/')
      toast.info(submitCommentStatus.message)
    })
  }

  const submitCyberComment = (comment: string) => {
    datadogLogs.logger.info("Submitting cyber assessment comment...");

    commentCyberAssessment({
      variables: {
        comment: comment
      }
    }).then((response) => {
      datadogLogs.logger.info("Cyber assessment comment submitted successfully.");
      const submitCommentStatus = response.data.commentCyberAssessment
      navigate('/')
      toast.info(submitCommentStatus.message)
    })
  }


  // Cyber Assessment specific functions
  const startCyberAssessment = ({ setCyberAssessmentData, setError }: ISubmitCyberResponseData) => {
    datadogLogs.logger.info("Starting Cyber assessment...");

    createCyberAssessment().then((response) => {
      datadogLogs.logger.info("Cyber assessment started successfully");
      setCyberAssessmentData(response.data.createCyberAssessment)
    })
      .catch((errors) => {
        console.error('Error Starting Cyber assessment:', errors.graphQLErrors.map((error: any) => error.message))
        setError && setError(errors.graphQLErrors[0].message)
      })
  }

  const submitCyberAssessment = ({ attributes, setCyberAssessmentData, setError }: ISubmitCyberResponseData, onSuccess?: Function, onError?: Function) => {
    datadogLogs.logger.info("Submitting Cyber assessment...");

    submitCyberAssessmentResponse({
      variables: {
        attributes: attributes
      }
    })
      .then((response) => {
        datadogLogs.logger.info("Cyber assessment submitted successfully");
        setCyberAssessmentData(response.data.submitCyberAssessment)
      })
      .then((response) => {
        if (onSuccess != null) {
          onSuccess()
        }
      })
      .catch((errors) => {
        console.error('Error Submitting Cyber assessment:', errors.graphQLErrors.map((error: any) => error.message))
        setError && setError(errors.graphQLErrors[0].message)
        if (onError != null) {
          onError()
        }
      })
  }

  return { getIntroPage, getCyberIntroPage, startPracticeTest, submitPracticeTest, startAssessment, submitAssessment, cancelAssessment, cancelCyberAssessment, submitComment, submitCyberComment, startCyberAssessment, submitCyberAssessment }
}

export default useAssessment
